import React, { lazy, Suspense, useState, useEffect } from 'react'
import { PageSpecificSchema } from '@ashleynexvelsolutions/page-specific-schema'
import { InteriorContentV2 } from '@ashleynexvelsolutions/interior-content-v2'
import { InteriorHeroV12 } from '@ashleynexvelsolutions/interior-hero-v12'
import { Breadcrumb } from '@ashleynexvelsolutions/breadcrumb'
import { Seo2 } from '@ashleynexvelsolutions/seo2'
import { graphql } from 'gatsby'
import { Layout } from '../components/Layout'
import { StickyCallout } from '@ashleynexvelsolutions/sticky-callout'
import {
  SeoFunction,
  PageSpecificSchemaFunction,
  InteriorCardsFunction,
  InteriorHeroFunction,
  BreadcrumbFunction,
  ContentSection1Function,
  ContentSection2Function,
  Callout2Function,
  ContentSection3Function,
  RelatedLinksFunction,
  XpinsFunction,
  InteriorCouponFunction,
  SidebarReviewsFunction,
  Callout1Function,
  FaqFunction,
  ContactFunction,
  MultiStepFormFunction,
  StickyCalloutFunction,
  TestimonialVideosFunction,
  InteriorAccordionsFunction,
  InteriorCallButton1,
} from '../DataImports'

import { CouponV6 } from '@ashleynexvelsolutions/coupon-v6'

const FaqV6 = lazy(() =>
  import('@ashleynexvelsolutions/faq-v6').then(module => ({
    default: module.FaqV6,
  })),
)

const InteriorCardsV3 = lazy(() =>
  import('@ashleynexvelsolutions/interior-cards-v3').then(module => ({
    default: module.InteriorCardsV3,
  })),
)
const ContentSectionAccordions = lazy(() =>
  import('@ashleynexvelsolutions/content-section-accordions').then(module => ({
    default: module.ContentSectionAccordions,
  })),
)
const CalloutV18 = lazy(() =>
  import('@ashleynexvelsolutions/callout-v18').then(module => ({
    default: module.CalloutV18,
  })),
)
const XpinsV2 = lazy(() =>
  import('@ashleynexvelsolutions/xpins-v2').then(module => ({
    default: module.XpinsV2,
  })),
)
const SidebarReviews = lazy(() =>
  import('@ashleynexvelsolutions/sidebar-reviews').then(module => ({
    default: module.SidebarReviews,
  })),
)
const CalloutV17 = lazy(() =>
  import('@ashleynexvelsolutions/callout-v17').then(module => ({
    default: module.CalloutV17,
  })),
)
const ContactV11 = lazy(() =>
  import('@ashleynexvelsolutions/contact-v11').then(module => ({
    default: module.ContactV11,
  })),
)
const MultiStepFormV4 = lazy(() =>
  import('@ashleynexvelsolutions/multi-step-form-v4').then(module => ({
    default: module.MultiStepFormV4,
  })),
)
const Portal = lazy(() =>
  import('@ashleynexvelsolutions/portal').then(module => ({
    default: module.Portal,
  })),
)

const TestimonialVideos = lazy(() =>
  import('@ashleynexvelsolutions/testimonial-videos').then(module => ({
    default: module.TestimonialVideos,
  })),
)

const InteriorAccordions = lazy(() =>
  import('@ashleynexvelsolutions/interior-accordions').then(module => ({
    default: module.InteriorAccordions,
  })),
)
const ButtonV14 = lazy(() =>
  import('@ashleynexvelsolutions/button-v14').then(module => ({
    default: module.ButtonV14,
  })),
)

const Page = ({ data }) => {
  const [formModal, setFormModal] = useState(false)
  const [isBannerDismissed, setIsBannerDismissed] = useState(false)

  const handleButton = () => setFormModal(true)

  useEffect(() => {
    if (data.wpPage.title === 'Financing') {
      const details = document.querySelectorAll('.financingAccordions details')

      const handleClick = targetDetail => {
        details.forEach(detail => {
          if (detail !== targetDetail) {
            detail.removeAttribute('open')
          }
        })
      }

      details.forEach(detail => {
        detail.addEventListener('click', () => handleClick(detail))
      })

      return () => {
        details.forEach(detail => {
          detail.removeEventListener('click', () => handleClick(detail))
        })
      }
    }
  }, [])

  return (
    <Layout>
      <InteriorHeroV12 {...InteriorHeroFunction(data, handleButton)} />
      <div
        className={`bg-yellow text-black font-bold uppercase fixed bottom-[9.25rem] left-1/2 -translate-x-1/2 lg:left-3/4 xl:translate-x-0 xl:bottom-48 z-50 scale-75 xl:scale-100 rounded-[0.8rem] lg:animate-bounceOnce w-[18.4375rem] shadow-lg flex items-center justify-center py-2 transition-opacity duration-300 ${isBannerDismissed && '!opacity-0'}`}
      >
        <p>0% Financing on All Repairs</p>
        <button
          onClick={() => setIsBannerDismissed(true)}
          className="ml-2 hover:opacity-70 transition-opacity absolute top-1 right-2 text-[0.625rem]"
          aria-label="Close banner"
        >
          ✕
        </button>
      </div>
      <StickyCallout {...StickyCalloutFunction(handleButton)} />
      <div className="border-b border-gray">
        <div className="container mx-auto py-4">
          <Breadcrumb {...BreadcrumbFunction(data)} />
        </div>
      </div>
      <div className="container mx-auto lg:grid lg:grid-cols-[30%_63%] lg:gap-x-[7%]">
        <div className="hidden lg:block">
          <div>
            <CouponV6 {...InteriorCouponFunction(handleButton, data)} />
          </div>

          <div className="rfs:mt-[4rem]">
            <SidebarReviews {...SidebarReviewsFunction()} />
          </div>
        </div>
        <div className="page rfs:mt-[6.1875rem]">
          <InteriorContentV2 {...ContentSection1Function(data)} />
          {data.wpPage.title === 'Reviews' && (
            <div className="mb-6">
              <div id={process.env.GATSBY_BIRDEYE_REVIEW_ID} />
            </div>
          )}
          <Suspense fallback="loading">
            <div className="max-w-[17.75rem]">
              <ButtonV14 {...InteriorCallButton1()} />
            </div>
          </Suspense>
        </div>
      </div>

      <Suspense fallback="loading">
        <div className="mx-auto">
          <TestimonialVideos {...TestimonialVideosFunction()} />
        </div>
      </Suspense>
      <Suspense fallback="loading">
        <div className="lg:rfs:mt-[7.125rem]">
          <InteriorCardsV3 {...InteriorCardsFunction(data)} />
        </div>
      </Suspense>
      <div className="container mx-auto mt-[3.5rem]">
        <div className="lg:hidden">
          <div>
            <CouponV6 {...InteriorCouponFunction(handleButton, data)} />
          </div>

          <div className="rfs:mt-[4rem]">
            <SidebarReviews {...SidebarReviewsFunction()} />
          </div>
        </div>
        {data.wpPage.title !== 'Service Areas' && (
          <div className="rfs:mb-[20rem] rfs:mt-[9rem]">
            <Suspense fallback="loading">
              <CalloutV17 {...Callout1Function(handleButton)} />
            </Suspense>
          </div>
        )}
      </div>
      <Suspense fallback="loading">
        {data.wpPage.nexvelschemapagesposts.questionsAndAnswers !== null && (
          <FaqV6 {...FaqFunction(data)} />
        )}
      </Suspense>
      {data.wpPage.contentSection2.contentSection2.title !== null && (
        <Suspense fallback="loading">
          <ContentSectionAccordions {...ContentSection2Function(data)} />
        </Suspense>
      )}
      {data.wpPage.title !== 'Service Areas' && (
        <Suspense fallback="loading">
          <CalloutV18 {...Callout2Function(handleButton)} />
        </Suspense>
      )}

      <Suspense fallback="loading">
        {data.wpPage.contentSection3.contentSection3.contentSection3 !== null && (
          <div className="bg-tan rfs:py-24">
            {data.wpPage.contentSectionAccordion.contentSectionAccordion.accordion !== null && (
              <Suspense fallback="loading">
                <div className="container mx-auto max-w-[86rem]">
                  <InteriorAccordions {...InteriorAccordionsFunction(data)} />
                </div>
              </Suspense>
            )}
            <div className="container page mx-auto max-w-[86rem]">
              <InteriorContentV2 {...ContentSection3Function(data)} />
              {data.wpPage.contentSection3.contentSection3.relatedLinks !== undefined && (
                <InteriorContentV2 {...RelatedLinksFunction(data)} />
              )}
            </div>
          </div>
        )}
      </Suspense>
      <div className="container mx-auto text-center">
        <a href="tel:888-509-3138" className="py-8 sm:pt-12 lg:rfs:pt-24 block">
          <p className="toptitle text-orange uppercase mb-6">
            Do you have an emergency? <br />
            <span className="lg:hidden">Click here to send a technician immediately.</span>
          </p>
          <div className="w-56 h-56 rounded-full bg-orange hover:bg-red active:bg-red text-white font-bold text-[1.5rem] lg:rfs:text-[1.5625rem] flex items-center justify-center mx-auto shadow-[0_8px_16px_rgba(0,0,0,0.2)] hover:shadow-[0_12px_24px_rgba(0,0,0,0.3)] transition-all duration-300 border-[0.75rem] border-t-yellow hover:border-t-red-dark active:border-t-red-dark border-l-yellow hover:border-l-red-dark active:border-l-red-dark border-b-red-dark hover:border-b-yellow active:border-b-yellow border-r-red-dark hover:border-r-yellow active:border-r-yellow hover:scale-105 active:scale-105">
            <p>
              Call Now!
              <br />
              <span className="hidden lg:block">888-509-3138</span>
            </p>
          </div>
        </a>
      </div>
      <Suspense fallback="loading">
        <XpinsV2 {...XpinsFunction(data)} />
      </Suspense>
      <Suspense fallback="loading">
        <ContactV11 {...ContactFunction()} />
      </Suspense>
      <Suspense fallback="loading">
        {formModal === true && (
          <Portal
            isOpen
            onClose={() => setFormModal(false)}
            xColor="text-black"
            maxWidth="max-w-full"
            padding="p-0"
            width="w-full lg:w-auto"
            background="bg-white/[0.9]"
          >
            <MultiStepFormV4 {...MultiStepFormFunction()} />
          </Portal>
        )}
      </Suspense>
      <PageSpecificSchema {...PageSpecificSchemaFunction(data)} />
    </Layout>
  )
}
export const query = graphql`
  query ($id: String!) {
    wpPage(id: { eq: $id }) {
      seo {
        title
        metaDesc
        opengraphTitle
        opengraphDescription
        opengraphUrl
        opengraphSiteName
        opengraphType
        opengraphImage {
          localFile {
            publicURL
          }
        }
        canonical
        twitterTitle
        twitterDescription
        twitterImage {
          localFile {
            publicURL
          }
        }
      }
      nexvelschemapagesposts {
        videos {
          title
          url
          description
          thumbnailImage {
            localFile {
              publicURL
            }
          }
          uploadDate
        }
        questionsAndAnswers {
          question
          answer
        }
        maps {
          mapUrl
        }
        digitaldocuments {
          title
        }
        images {
          image {
            localFile {
              publicURL
            }
            date(formatString: "LL")
            description
            title
          }
        }
        areaServedLandingPageSchema
        serviceSchema {
          serviceNameSchema
          linkToRelevantServicePageSchema
        }
        keywordsSchema {
          keywordSchema
        }
        localAwardsSchema {
          localAwardSchema
        }
        latitudeSchema
        longitudeSchema
        relevantExternalPagesSchema {
          relevantExternalPageSchema
        }
      }
      title
      uri
      interiorCoupon {
        interiorCoupon {
          title
          subtitle
          text
        }
      }
      breadcrumbs {
        breadcrumbs {
          breadcrumbs
        }
      }
      hero {
        hero {
          backgroundImageDesktop {
            sourceUrl
          }
          title
          text
        }
      }
      contentSection1 {
        contentSection1 {
          contentSection1
        }
      }
      faqs {
        faqs {
          title
          subtitle
        }
      }
      contentSection2 {
        contentSection2 {
          title
          contentSection2
          image {
            sourceUrl
            mediaDetails {
              width
              height
            }
          }
          imageAltText
          accordion {
            label
            content
          }
        }
      }
      contentSectionAccordion {
        contentSectionAccordion {
          accordion {
            label
            content1
            content2
            image {
              sourceUrl
            }
          }
        }
      }
      contentSection3 {
        contentSection3 {
          contentSection3
          relatedLinks
        }
      }
      xPins {
        xPins {
          mapCenter {
            latitude
            longitude
          }
          radius
          cards {
            date
            description
            image {
              sourceUrl
              mediaDetails {
                width
                height
              }
            }
          }
        }
      }
    }
    wp {
      nexvelSchemaMarkup {
        nexvelschema {
          cities {
            city
          }
          services {
            service
            linkToRelevantPageOnYourWebsite
          }
        }
      }
    }
    site {
      siteMetadata {
        siteUrl
      }
    }
  }
`
export default Page
export const Head = ({ data }) => <Seo2 {...SeoFunction(data)} />
